import { useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { SimpleApplication, Program, User, Type } from '../../../../models/UserServerData';
import { NS } from '../../../../models/I18n';
import { SubApplicant, SubApplicantOption } from '../../../../models/SubApplicant';
import { getItemFromLocalStorage } from '../../../../shared/utils/storage-api';
import { findApplicationsForProgram } from '../../../../shared/utils/findOldestApplicationForProgram';
import { HistoryBackPromptSwitch } from '../../../../shared/components/HistoryBackPrompt';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '../../../../shared/components/ui/AlertDialog';
import { Button } from '../../../../shared/components/ui/Button';
import { AssignApplicantForm } from '../../../../shared/components/AssignApplicantForm';
import ExternalLink from '../../../../shared/components/links/ExternalLink';
import DynamicTables from '../../../../shared/views/DynamicTables';
import { useProgramQuery, useProgramServiceQuery } from '../../../SubmissionAPI';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../../../../shared/components/ui/Dialog';
import { ServiceFee } from '../../../../shared/components/ServiceFee';
import { cn } from '../../../../shared/utils/cn';
import { Select, SelectContent, SelectItem, SelectValue } from '../../../../shared/components/ui/Select';
import { SelectButtonTrigger, getApplicantName } from '../../../../shared/components/GoToExsitingApplication';

interface ProgramCardProps {
  applications?: SimpleApplication[];
  program: Program;
  createApplication?: (
    program: Program,
    subApplicant?: { firstName: string; lastName: string; id: number | null },
    isAssignedToSelf?: boolean,
  ) => void;
  showConfirmation?: boolean;
  subApplicants?: SubApplicant[];
  user?: User;
}

const StartApplicationButton = ({
  createApplication,
  program,
  fullButton,
}: {
  program: Program;
  createApplication?: (program: Program) => Promise<void>;
  fullButton: boolean;
}) => {
  const { t } = useTranslation(NS.Shared);
  return (
    <>
      {createApplication && (
        <Button
          className={cn('w-full lg:w-auto', { 'lg:flex-1': fullButton })}
          onClick={() => createApplication(program)}
        >
          {t('program.start-application')}
        </Button>
      )}
    </>
  );
};

const AssignApplicantContent = ({
  createApplication,
  program,
  user,
  setOpen,
  subApplicants,
  applications,
}: {
  createApplication?: (
    program: Program,
    subApplicant?: { firstName: string; lastName: string; id: number | null },
    isAssignedToSelf?: boolean,
  ) => void;
  program: Program;
  user?: User;
  setOpen: (open: boolean) => void;
  subApplicants: SubApplicant[];
  applications: SimpleApplication[];
}) => {
  const [subApplicantId, setSubApplicantId] = useState<number | undefined | null>(undefined);
  const [subApplicant, setSubApplicant] = useState<SubApplicantOption>('myself');
  const ref = useRef<HTMLFormElement>(null);
  const { t } = useTranslation([NS.Shared, NS.Eligibility]);

  const onSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (ref?.current && createApplication) {
      const data = new FormData(ref.current);
      createApplication(
        program,
        subApplicant === 'someone_else'
          ? ({
              id: subApplicantId || null,
              firstName: data.get('first_name') as string,
              lastName: data.get('last_name') as string,
            } as any)
          : undefined,
        subApplicant !== 'someone_else',
      );
    }
  };
  const selectedLng = getItemFromLocalStorage('i18nextLng') || 'en';

  const matchedApplications = findApplicationsForProgram({
    programId: program.id,
    applicantType: subApplicant,
    subApplicantId,
    applications,
  });
  return (
    <form ref={ref} onSubmit={onSubmit}>
      <AlertDialogHeader className="mb-4">
        <AlertDialogTitle>{t(`${NS.Eligibility}:eligibleProgramCards.start-new-application`)}</AlertDialogTitle>
      </AlertDialogHeader>
      <AssignApplicantForm
        subApplicant={subApplicant}
        setSubApplicant={setSubApplicant}
        subApplicantId={subApplicantId}
        setSubApplicantId={setSubApplicantId}
        existingApplicationId={matchedApplications.length > 0 ? matchedApplications[0].id : null}
        program={program}
        user={user}
      />
      <AlertDialogFooter className="mt-8">
        <Button
          onClick={() => setOpen(false)}
          variant="secondary"
          className="mb-2 w-full lg:mb-0 lg:w-auto lg:min-w-[120px]"
        >
          {t('program.cancel')}
        </Button>
        {matchedApplications.length > 0 && (
          <Button
            variant="outline"
            className="w-full lg:w-auto lg:min-w-[160px] lg:whitespace-nowrap lg:text-center"
            type="submit"
            disabled={subApplicant === 'someone_else' && subApplicants.length > 0 && subApplicantId === undefined}
          >
            {t('program.start-application')}
          </Button>
        )}
        {matchedApplications.length === 1 && (
          <ExternalLink
            variant="primary"
            className="text-center lg:whitespace-nowrap"
            to={`https://apply.${window.location.host}/user/applications/${matchedApplications[0].id}?lng=${selectedLng}`}
          >
            {t('program.go-to-existing-application')}
          </ExternalLink>
        )}
        {matchedApplications.length > 1 && (
          <Select
            onValueChange={(value) => {
              if (!value) {
                return;
              }
              window.location.href = `https://apply.${window.location.host}/user/applications/${value}?lng=${selectedLng}`;
            }}
          >
            <SelectButtonTrigger className="h-9 lg:w-auto lg:min-w-[230px]">
              <SelectValue asChild placeholder={t('program.go-to-existing-application')}>
                <span>{t('program.go-to-existing-application')}</span>
              </SelectValue>
            </SelectButtonTrigger>
            <SelectContent className="z-[1000] max-h-[200px]">
              {matchedApplications.map((app) => {
                const applicantName = getApplicantName({
                  application: app,
                  user,
                  textUnassigned: t('supertable.unassigned'),
                  textMyself: t(`${NS.Eligibility}:eligibleProgramCards.myself`),
                });
                return (
                  <SelectItem key={app.id} value={`${app.id}`} className="hover:bg-slate-100">
                    <span className="mr-1 font-mono text-slate-400">{app.id}</span>
                    <span className="inline-block max-w-[180px] truncate align-top">{applicantName}</span>
                  </SelectItem>
                );
              })}
            </SelectContent>
          </Select>
        )}
        {matchedApplications.length === 0 && (
          <Button
            variant="primary"
            className="w-full lg:w-auto lg:min-w-[120px]"
            type="submit"
            disabled={subApplicant === 'someone_else' && subApplicants.length > 0 && subApplicantId === undefined}
          >
            {t('program.start-application')}
          </Button>
        )}
      </AlertDialogFooter>
    </form>
  );
};

const StartApplicationButtonWithConfirmation = ({
  createApplication,
  program,
  subApplicants,
  applications,
  user,
  fullButton,
}: {
  program: Program;
  createApplication?: (
    program: Program,
    subApplicant?: { firstName: string; lastName: string; id: number | null },
    isAssignedToSelf?: boolean,
  ) => void;
  subApplicants: SubApplicant[];
  applications: SimpleApplication[];
  user?: User;
  fullButton: boolean;
}) => {
  const [open, setOpen] = useState(false);
  const { t } = useTranslation([NS.Shared, NS.Eligibility]);

  if (!createApplication) {
    return null;
  }
  return (
    <HistoryBackPromptSwitch onHistoryBack={() => setOpen(false)}>
      <AlertDialog open={open} onOpenChange={setOpen}>
        <AlertDialogTrigger
          asChild
          onClick={(e) => {
            e.stopPropagation();
            setOpen(true);
          }}
        >
          <Button className={fullButton ? 'lg:flex-1' : ''}>{t('program.start-application')}</Button>
        </AlertDialogTrigger>
        <AlertDialogContent className="max-w-xl">
          {open && (
            <AssignApplicantContent
              createApplication={createApplication}
              program={program}
              user={user}
              setOpen={setOpen}
              subApplicants={subApplicants}
              applications={applications}
            />
          )}
        </AlertDialogContent>
      </AlertDialog>
    </HistoryBackPromptSwitch>
  );
};

function GetStartedButton({
  showConfirmation,
  createApplication,
  program,
  subApplicants,
  applications,
  user,
  fullButton = false,
}: {
  showConfirmation: boolean;
  createApplication?: (
    program: Program,
    subApplicant?: { firstName: string; lastName: string; id: number | null },
    isAssignedToSelf?: boolean,
  ) => void;
  program: Program;
  subApplicants: SubApplicant[];
  applications: SimpleApplication[];
  user?: User;
  fullButton?: boolean;
}) {
  return (
    <>
      {showConfirmation ? (
        <StartApplicationButtonWithConfirmation
          createApplication={createApplication}
          program={program}
          subApplicants={subApplicants}
          applications={applications}
          user={user}
          fullButton={fullButton}
        />
      ) : (
        <StartApplicationButton
          createApplication={createApplication as (program: Program) => Promise<void>}
          program={program}
          fullButton={fullButton}
        />
      )}
    </>
  );
}

function ProgramCardLoading() {
  return (
    <div className="flex h-[400px] min-w-[300px] animate-pulse flex-col justify-between rounded-lg bg-gray-200 p-4 shadow lg:w-[400px]" />
  );
}

function ProgramDynamicTables({ program }: { program: Program }) {
  const { isLoading, data: programServices } = useProgramServiceQuery({ programId: program.id });
  if (isLoading) {
    return null;
  }
  return <DynamicTables programServices={programServices} selectedVisa={program} />;
}

function ProgramMoreContent({ program, children }: { program: Program; children: React.ReactNode }) {
  return (
    <>
      <DialogHeader>
        <DialogTitle>{program.name}</DialogTitle>
      </DialogHeader>
      <section
        className="prose-classic"
        dangerouslySetInnerHTML={{ __html: program.sanitized_description_and_summary.description }}
      />
      <ProgramDynamicTables program={program} />
      <DialogFooter>{children}</DialogFooter>
    </>
  );
}

const ProgramCard = ({
  applications = [],
  program,
  createApplication,
  showConfirmation = false,
  subApplicants = [],
  user,
}: ProgramCardProps) => {
  const { t } = useTranslation([NS.Shared, NS.Eligibility]);
  const [moreOpen, setMoreOpen] = useState(false);
  return (
    <div className="flex min-w-[300px] flex-col justify-between rounded-lg bg-white p-4 shadow lg:w-[400px]">
      <section className="flex flex-1 flex-col">
        <h3 className="mb-4 text-left text-base font-bold text-gray-600 lg:text-lg">{program.name}</h3>
        <div className="flex-1 rounded-lg bg-gray-50 p-4 text-sm leading-relaxed text-gray-500 shadow-inner">
          <div>
            <span className="text-xs font-light">{`${t('program.type')}: `}</span>
            <span className="font-medium capitalize">{t(`eligibility:typeSelection.${program.program_type}`)}</span>
          </div>
          <div>
            <span className="text-xs font-light">{`${t('program.government-processing-time')}: `}</span>
            <span className="font-medium">{program.government_time}</span>
          </div>
          <div>
            {program.government_fee.amount !== 0 && (
              <>
                <span className="text-xs font-light">{`${t('program.government-fee')}: `}</span>
                <span className="font-medium">{program.government_fee.formatted_price}</span>
              </>
            )}
          </div>
          <div>
            <span className="text-xs font-light">{`${t('program.supervisas-service-fee')}: `}</span>
            <ServiceFee
              amount={program.price.amount}
              formattedPrice={program.price.formatted_price}
              currency={program.price.currency}
            />
          </div>
          <div
            className="mt-4"
            dangerouslySetInnerHTML={{ __html: program.sanitized_description_and_summary.summary }}
          />
        </div>
      </section>
      <div className="mt-6 flex w-full flex-col space-y-2 lg:flex-row lg:gap-4 lg:space-y-0">
        <HistoryBackPromptSwitch onHistoryBack={() => setMoreOpen(false)}>
          <Dialog open={moreOpen} onOpenChange={setMoreOpen}>
            <DialogTrigger asChild>
              <Button variant="secondary" className="lg:flex-1">
                {t('program.more-info')}
              </Button>
            </DialogTrigger>
            <DialogContent className="max-h-[85vh] overflow-y-scroll lg:w-auto lg:max-w-none lg:p-6">
              <ProgramMoreContent program={program}>
                <GetStartedButton
                  showConfirmation={showConfirmation}
                  createApplication={createApplication}
                  program={program}
                  subApplicants={subApplicants}
                  applications={applications}
                  user={user}
                />
              </ProgramMoreContent>
            </DialogContent>
          </Dialog>
        </HistoryBackPromptSwitch>
        <GetStartedButton
          showConfirmation={showConfirmation}
          createApplication={createApplication}
          program={program}
          subApplicants={subApplicants}
          applications={applications}
          user={user}
          fullButton
        />
      </div>
    </div>
  );
};

interface DynamicProgramCardProps {
  programId: number;
  filterTypes: Set<Type>;
  createApplication?: (
    program: Program,
    subApplicant?: { firstName: string; lastName: string; id: number | null },
    isAssignedToSelf?: boolean,
  ) => void;
  applications?: SimpleApplication[];
  subApplicants?: SubApplicant[];
  user?: User;
}

export function DynamicProgramCard({
  programId,
  filterTypes,
  createApplication,
  applications,
  subApplicants,
  user,
}: DynamicProgramCardProps) {
  const { data: program } = useProgramQuery({ programId });
  if (!program) {
    return <ProgramCardLoading />;
  }
  if (filterTypes.size > 0 && !filterTypes.has(program.program_type)) {
    return null;
  }
  return (
    <ProgramCard
      program={program}
      createApplication={createApplication}
      {...(user && {
        showConfirmation: true,
        applications,
        subApplicants,
        user,
      })}
    />
  );
}
